import { gql } from 'apollo-boost';

import { minusPhraseLibraryConstantsFragment } from './minus-phrase';

export const constantsCurrencyFragment = gql`
    fragment ConstantsCurrencyQueryFragment on GdCurrencyDescription {
        code
        minDailyBudget
        maxDailyBudget
        bigRate
        minPrice
        maxPrice
        bigRate
        defaultPrice
        minCpmPrice
        maxCpmPrice
        autobudgetAvgCpaWarning
        autobudgetAvgPriceWarning
        autobudgetClicksBundleWarning
        autobudgetMaxPriceWarning
        autobudgetSumWarning
        maxAutobudget
        maxAutobudgetBid
        maxAutobudgetClicksBundle
        minAutobudget
        minAutobudgetBid
        minAutobudgetClicksBundle
        minAutobudgetAvgCpa
        minAutobudgetAvgPrice
    }
`;

export const constantsFragment = gql`
    fragment ConstantsQueryFragment on GdDirectConstants {
        agencyOfflineReportMaximumDate
        strategyConstants {
            maxDailyBudgetChangesPerDay
        }
        currencyConstants {
            ...ConstantsCurrencyQueryFragment
        }
        alertData {
            enabled
            textRu
            textEn
        }
        validation {
            libMinusKeywordsConstants {
                ...minusPhraseLibraryConstantsFragment
            }
            adGroupConstants {
                maxAdGroupsCountPerUpdate
            }
            campaignConstants {
                minWarningBalanceInPercent
                maxWarningBalanceInPercent
            }
        }
        defaultValues {
            campaign {
                defaultWarningBalance
                defaultCheckPositionInterval
                defaultSmsTimeInterval {
                    endTime {
                        hour
                        minute
                    }
                    startTime {
                        hour
                        minute
                    }
                }
            }
        }
        configuration {
            balanceDomain
            canvasUiDomain
            spravIFrameUrl
        }
    }
    ${constantsCurrencyFragment}
    ${minusPhraseLibraryConstantsFragment}
`;

export const campaignsContextQuery = gql`
    fragment CampaignContextFragment on GdClientAccess {
        canCreateCampaignTypes
        canCreateCampaign
        canViewInternalAds
        canEditInternalAds
        canManageLibMinusWords
        canManageInternalAdAccessRights
    }
`;
export const footerQueryUserInfo = gql`
    fragment FooterQueryUserInfoFragment on GdUserInfo {
        name
        login
        email
        phone
    }
`;

export const operatorFeaturesFragment = gql`
    fragment OperatorFeaturesFragment on GdOperatorFeatures {
        isAdGroupUpdateAllowed
        isBannerUpdateAllowed
        isCpmBannerUpdateAllowed
        isBannerAimingAllowed
        isBannerAimingCpmAllowed
        isPerformanceAdEditAllowed
        isOutdoorGroupsEditAllowed
        isIndoorGroupsEditAllowed
        isRetargetingGridEnabled
        isAdsMassEditAllowed
        isCampaignUpdateAllowed
        isCpmAdGroupUpdateAllowed
        isVideoGroupsEditAllowed
        isGeoproductGroupsEditAllowed
        isAudioGroupsEditAllowed
        isMcBannerDnaEnabled
    }
`;

export const footerQueryOfficeInfo = gql`
    fragment FooterQueryOfficeInfoFragment on GdOfficeContactData {
        description
        cityName
        phone
        phoneExtension
    }
`;

export const footerQueryOperator = gql`
    fragment FooterQueryOperatorFragment on GdOperatorInfo {
        tuneSecretKey
        workCurrency
        isSelfAgency
        isAgency
        info {
            login
        }
        access {
            canEditInternationalization
            canSearchPhrasesDuplicates
            canEditAdDomain
            canViewInternalAdsInSomeClients
            actions
        }
        officeInfo {
            cityContact {
                ...FooterQueryOfficeInfoFragment
            }
            regionContact {
                ...FooterQueryOfficeInfoFragment
            }
            regionContact2 {
                ...FooterQueryOfficeInfoFragment
            }
        }
        managerInfo {
            ...FooterQueryUserInfoFragment
        }
        chiefInfo {
            ...FooterQueryUserInfoFragment
        }
        agencyInfo {
            ...FooterQueryUserInfoFragment
        }
    }
    ${footerQueryOfficeInfo}
    ${footerQueryUserInfo}
`;

export const footerQueryAgencyInfo = gql`
    fragment FooterQueryAgencyInfoFragment on GdAgencyInfo {
        clientId
        userId
        name
        login
        email
        phone
        representativeName
    }
`;

export const footerQueryClient = gql`
    fragment FooterQueryClientFragment on GdClient {
        info {
            id
            workCurrency
            countryRegionId
            translocalGeotreeType
            autoVideo
            managersInfo {
                ...FooterQueryUserInfoFragment
            }
            agencyInfo {
                ...FooterQueryAgencyInfoFragment
            }
        }
        access {
            canPayByCash
            canManageFavoriteCampaigns
            canCopyCampaign
            canManageUsingCommander
            canMonitorPhrasesPositions
            canTransferMoney
            operatorIsClient
            canUploadDocuments
            canManageUsingXLS
            canManageRetargertingConditions
            canManageTurboLandings
            canSeeClientRepresentatives
            canManageVCards
            canViewOfflineReports
            canManageUsingApi
            canCreateCampaign
            canCreateCampaignTypes
            operatorIsClientChief
            canManageCreatives
            canManageFeeds
            canCheckCampaignsStats
            canViewUserLogs
            canViewMobileApps
            canSeeDealsLinkEverywhere
            canSeeAgencyClients
        }
    }
    ${footerQueryUserInfo}
    ${footerQueryAgencyInfo}
`;

export const walletsData = gql`
    fragment WalletsDataFragment on GdWallet {
        id
        status {
            enabled
        }
        budget {
            sum
            showMode
            type
        }
        sum
        currency
        actions
    }
`;

export const commonQuery = gql`
    query CommonQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            wallets {
                ...WalletsDataFragment
            }
            access {
                ...CampaignContextFragment
            }
            features {
                isGridEnabled
                isGoalsStatEnabled
                isMinusWordsLibEnabled
                isInternalAdsAllowed
                isBidCorrectionSearchEnabled
                isCpmCampaignsEnabled
                isTextCampaign8SitelinksEnabled
                isTycoonOrganizationsEnabled
                isCpmVideoSeveralPixelsEnabled
                isEmptyRetCondInCpmGroupEnabled
                isCpmIndoorSegmentsEnabled

                isCpcDeviceModifiersAllowed

                isAdmetricaMeasurerCpmBannerAllowed
                isAdmetricaMeasurerCpmDealsAllowed
                isAdmetricaMeasurerCpmGeoproductAllowed
                isAdmetricaMeasurerCpmVideoAllowed
                isAdmetricaMeasurerCpmYndxFrontpageAllowed

                isAdlooxMeasurerCpmBannerAllowed
                isAdlooxMeasurerCpmDealsAllowed
                isAdlooxMeasurerCpmGeoproductAllowed
                isAdlooxMeasurerCpmVideoAllowed
                isAdlooxMeasurerCpmYndxFrontpageAllowed

                isAdriverMeasurerCpmBannerAllowed
                isAdriverMeasurerCpmDealsAllowed
                isAdriverMeasurerCpmGeoproductAllowed
                isAdriverMeasurerCpmVideoAllowed
                isAdriverMeasurerCpmYndxFrontpageAllowed

                isDvMeasurerCpmBannerAllowed
                isDvMeasurerCpmDealsAllowed
                isDvMeasurerCpmGeoproductAllowed
                isDvMeasurerCpmVideoAllowed
                isDvMeasurerCpmYndxFrontpageAllowed

                isIntegralAdScienceMeasurerCpmBannerAllowed
                isIntegralAdScienceMeasurerCpmDealsAllowed
                isIntegralAdScienceMeasurerCpmGeoproductAllowed
                isIntegralAdScienceMeasurerCpmVideoAllowed
                isIntegralAdScienceMeasurerCpmYndxFrontpageAllowed

                isMediascopeMeasurerCpmBannerAllowed
                isMediascopeMeasurerCpmDealsAllowed
                isMediascopeMeasurerCpmGeoproductAllowed
                isMediascopeMeasurerCpmVideoAllowed
                isMediascopeMeasurerCpmYndxFrontpageAllowed

                isMoatMeasurerCpmBannerAllowed
                isMoatMeasurerCpmDealsAllowed
                isMoatMeasurerCpmGeoproductAllowed
                isMoatMeasurerCpmVideoAllowed
                isMoatMeasurerCpmYndxFrontpageAllowed

                isSizmekMeasurerCpmBannerAllowed
                isSizmekMeasurerCpmDealsAllowed
                isSizmekMeasurerCpmGeoproductAllowed
                isSizmekMeasurerCpmVideoAllowed
                isSizmekMeasurerCpmYndxFrontpageAllowed

                isTnsEnabled
            }
            ...FooterQueryClientFragment
        }
        constants {
            ...ConstantsQueryFragment
        }
        operator {
            ...FooterQueryOperatorFragment
            features {
                ...OperatorFeaturesFragment
            }
        }
    }
    ${footerQueryClient}
    ${footerQueryOperator}
    ${constantsFragment}
    ${campaignsContextQuery}
    ${walletsData}
    ${operatorFeaturesFragment}
`;

export const findAndReplaceKeywordsPreview = gql`
    query FindAndReplaceKeywordsPreview(
        $input: GdFindAndReplaceKeywordsInput!
    ) {
        reqId: getReqId
        getFindAndReplaceKeywordsPreview(input: $input) {
            cacheKey
            totalCount
            rowset {
                minusKeywords
                changedMinusKeywords
                keyword
                changedKeyword
            }
            validationResult {
                errors {
                    path
                    code
                    params
                }
            }
        }
    }
`;

export const commonWallet = gql`
    query CommonWalletQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            wallets {
                sum
                currency
                status {
                    enabled
                }
            }
        }
    }
`;

export const balanceCartDataQuery = gql`
    query BalanceCartDataQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            wallets {
                id
            }
            features {
                isB2bBalanceCartEnabled
            }
        }
    }
`;

export const dealsCount = gql`
    query DealsCount($login: String!, $dealsFilter: GdiDealFilterInput) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            deals(filter: $dealsFilter) {
                totalCount
            }
        }
    }
`;

export const headerClientInfo = gql`
    query HeaderClientInfoQuery($login: String!) {
        client(searchBy: { login: $login }) {
            info {
                id
                chiefUserId
                agencyClientId
            }
            features {
                isMinusWordsLibEnabled
            }
            wallets {
                ...WalletsDataFragment
            }
        }
    }
    ${walletsData}
`;

export const headerMenu = gql`
    query MenuHeaderQuery {
        menuHeader {
            allowedMenuItems
        }
    }
`;

export const headerInfo = gql`
    query HeaderInfoQuery($login: String!, $dealsFilter: GdiDealFilterInput) {
        getReqId
        client(searchBy: { login: $login }) {
            info {
                id
                chiefUserId
                agencyClientId
            }
            deals(filter: $dealsFilter) {
                totalCount
            }
            features {
                isMinusWordsLibEnabled
                isB2bBalanceCartEnabled
            }
            access {
                canSeeDealsLinkEverywhere
            }
            wallets {
                ...WalletsDataFragment
            }
        }
        operator {
            info {
                userId
            }
            isAgency
        }
    }
    ${walletsData}
`;

export const walletInfo = gql`
    query WalletInfoQuery($login: String!) {
        getReqId
        client(searchBy: { login: $login }) {
            wallets {
                ...WalletsDataFragment
            }
        }
    }
    ${walletsData}
`;

export const checkClientMutationState = gql`
    query CheckClientMutationStateQuery(
        $input: GdCheckClientMutationStateInput!
    ) {
        checkClientMutationState(input: $input) {
            state
        }
    }
`;

export const authorizationQuery = gql`
    query AuthorizationQuery {
        operator {
            chiefInfo {
                login
                clientId
                userId
            }
            isFreelancer
        }
    }
`;

export const clientMetrikaCountersQuery = gql`
    query ClientMetrikaCountersQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            metrikaCounters {
                counters
                isMetrikaAvailable
            }
        }
    }
`;
